import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { marked, Renderer } from "marked";

/**
 * Premium luxury PDF blueprint renderer.
 *
 * v3 — Block-aware paginator
 *  - Real page margins with reserved footer safe-area (no text touching page numbers)
 *  - Word/element splitting eliminated: top-level blocks are packed into pages, never sliced
 *  - Oversized blocks (very tall tables/pre) gracefully fall back to canvas-slice across pages
 *  - Premium per-section visual variety: rotating banner gradients + accent side rail
 *  - Auto-injected Reflection + Weekly Goals worksheet (toggleable)
 *  - Pro Tips & Warnings index, Premium TOC, consistent footer numbering
 */

export interface PremiumGuidePdfOptions {
  subtitle?: string;
  accentColor?: string;
  accentSecondary?: string;
  brandName?: string;
  authorLine?: string;
  includeReflection?: boolean;
  runQa?: boolean;
}

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

// Page chrome dimensions (must match CSS below)
const PAGE_TOP_PADDING = 120;
const PAGE_BOTTOM_PADDING = 140; // footer safe-area (page number lives here)
const PAGE_HEADER_HEIGHT = 150;
const USABLE_PX = A4_HEIGHT_PX - PAGE_TOP_PADDING - PAGE_BOTTOM_PADDING - PAGE_HEADER_HEIGHT;

// ---------- Markdown → premium HTML ----------

function enhanceCallouts(html: string, accent: string) {
  let counter = 0;
  return html.replace(
    /<blockquote>\s*<p>\s*<strong>([^<]+?)<\/strong>([\s\S]*?)<\/p>\s*<\/blockquote>/g,
    (_m, rawLabel: string, rest: string) => {
      const label = rawLabel.replace(/[:\s]+$/g, "").trim();
      const lower = label.toLowerCase();
      let kind: "tip" | "mistake" | "insight" | "action" | "reflect" | "note" = "note";
      if (/(pro\s*tip|^tip|hack|secret)/.test(lower)) kind = "tip";
      else if (/(mistake|warning|avoid|pitfall|caution)/.test(lower)) kind = "mistake";
      else if (/(insight|key|principle|truth|takeaway)/.test(lower)) kind = "insight";
      else if (/(action|do this|task|exercise|implement)/.test(lower)) kind = "action";
      else if (/(reflect|journal|question|ask yourself)/.test(lower)) kind = "reflect";

      const palette: Record<string, { bg: string; bd: string; fg: string; icon: string; title: string }> = {
        tip:     { bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", bd: accent,    fg: "#78350f", icon: "✦", title: label || "Pro Tip" },
        mistake: { bg: "linear-gradient(135deg,#fef2f2,#fee2e2)", bd: "#ef4444", fg: "#7f1d1d", icon: "⚠", title: label || "Avoid This Mistake" },
        insight: { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", bd: "#3b82f6", fg: "#1e3a8a", icon: "◆", title: label || "Key Insight" },
        action:  { bg: "linear-gradient(135deg,#ecfdf5,#d1fae5)", bd: "#10b981", fg: "#064e3b", icon: "▶", title: label || "Action Step" },
        reflect: { bg: "linear-gradient(135deg,#f5f3ff,#ede9fe)", bd: "#8b5cf6", fg: "#4c1d95", icon: "✎", title: label || "Reflection" },
        note:    { bg: "linear-gradient(135deg,#f8fafc,#f1f5f9)", bd: "#64748b", fg: "#0f172a", icon: "❝", title: label || "Note" },
      };
      const p = palette[kind];
      const id = `pg-co-${counter++}`;
      const summary = rest.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 140);
      return `<div id="${id}" class="pg-callout pg-callout-${kind}" data-kind="${kind}" data-title="${escapeAttr(p.title)}" data-summary="${escapeAttr(summary)}" style="background:${p.bg};border-left:5px solid ${p.bd};color:${p.fg};">
        <div class="pg-callout-head">
          <span class="pg-callout-icon" style="background:${p.bd};">${p.icon}</span>
          <span class="pg-callout-title">${p.title}</span>
        </div>
        <div class="pg-callout-body">${rest.trim()}</div>
      </div>`;
    },
  );
}

function transformChecklists(html: string): string {
  return html
    .replace(/<li>\s*\[\s\]\s*/g, '<li class="pg-check"><span class="pg-box"></span>')
    .replace(/<li>\s*\[x\]\s*/gi, '<li class="pg-check pg-check-done"><span class="pg-box pg-box-done">✓</span>');
}

function decorateModuleHeadings(html: string): string {
  return html.replace(
    /<h3>([^<]*?(Module|Step|Framework|Strategy|Phase)[^<]*?)<\/h3>/gi,
    '<h3 class="pg-module-title"><span class="pg-module-chip">●</span>$1</h3>',
  );
}

function buildHtmlFromMarkdown(markdown: string, accent: string) {
  const renderer = new Renderer();
  renderer.hr = () => `<div class="pg-divider"><span></span><span></span><span></span></div>`;

  let html = marked.parse(removeAiGenerationReferences(markdown), { gfm: true, breaks: true, renderer }) as string;
  html = html
    .replace(/<table>/g, '<div class="pg-table-wrap"><table class="pg-table">')
    .replace(/<\/table>/g, "</table></div>");
  html = enhanceCallouts(html, accent);
  html = transformChecklists(html);
  html = decorateModuleHeadings(html);
  return html;
}

function splitIntoSections(html: string) {
  const parts = html.split(/(?=<h2>)/g);
  return parts.map((p) => {
    const m = p.match(/^<h2>([\s\S]*?)<\/h2>/);
    return { title: m ? m[1].replace(/<[^>]+>/g, "") : "", body: p };
  });
}

function chapterCloser(sectionTitle: string): string {
  const safe = escapeHtml(sectionTitle || "this chapter");
  return `
    <div class="pg-chapter-closer">
      <div class="pg-closer-grid">
        <div class="pg-closer-card pg-closer-reflect">
          <div class="pg-closer-eyebrow">Reflection</div>
          <div class="pg-closer-title">Pause &amp; integrate — ${safe}</div>
          <ul class="pg-closer-list">
            <li><span class="pg-q">1.</span> What is the single most important idea you took from this chapter?</li>
            <li><span class="pg-q">2.</span> Where in your current work are you under-applying it?</li>
            <li><span class="pg-q">3.</span> What belief or habit do you need to drop to fully use it?</li>
          </ul>
          <div class="pg-notes-lines"><div></div><div></div><div></div></div>
        </div>
        <div class="pg-closer-card pg-closer-goals">
          <div class="pg-closer-eyebrow">Weekly Goals</div>
          <div class="pg-closer-title">Your 7-day execution plan</div>
          <ul class="pg-week-list">
            <li class="pg-check"><span class="pg-box"></span><b>Day 1–2:</b> Restate the chapter in your own words.</li>
            <li class="pg-check"><span class="pg-box"></span><b>Day 3–4:</b> Apply the core framework to one real project.</li>
            <li class="pg-check"><span class="pg-box"></span><b>Day 5:</b> Identify one mistake to actively avoid this week.</li>
            <li class="pg-check"><span class="pg-box"></span><b>Day 6:</b> Teach the concept to someone else in 5 minutes.</li>
            <li class="pg-check"><span class="pg-box"></span><b>Day 7:</b> Audit progress &amp; commit to next chapter.</li>
          </ul>
        </div>
      </div>
    </div>`;
}

// ---------- Stylesheet ----------

function premiumStyles(accent: string, accent2: string) {
  return `
    * { box-sizing: border-box; }
    .pg-root { font-family: 'Inter','Helvetica Neue',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; color:#0f172a; }
    .pg-page { width:${A4_WIDTH_PX}px; height:${A4_HEIGHT_PX}px; padding:${PAGE_TOP_PADDING}px 64px ${PAGE_BOTTOM_PADDING}px; background:#ffffff; position:relative; overflow:hidden; }
    .pg-page::before { content:""; position:absolute; left:0; top:0; bottom:0; width:6px; background:linear-gradient(180deg, ${accent}, ${accent2}); z-index:0; }
    .pg-page::after { content:""; position:absolute; right:-120px; top:-120px; width:320px; height:320px; border-radius:50%; background:radial-gradient(circle, ${accent}10 0%, transparent 70%); pointer-events:none; z-index:0; }

    /* Cover */
    .pg-cover { width:${A4_WIDTH_PX}px; height:${A4_HEIGHT_PX}px; padding:0; background:radial-gradient(ellipse at 20% 0%, #1e293b 0%, #0b1120 55%, #050810 100%); color:#f8fafc; position:relative; overflow:hidden; }
    .pg-cover-glow { position:absolute; width:680px; height:680px; right:-260px; top:-180px; background:radial-gradient(circle, ${accent}55 0%, transparent 60%); filter:blur(20px); }
    .pg-cover-glow-2 { position:absolute; width:520px; height:520px; left:-200px; bottom:-220px; background:radial-gradient(circle, ${accent2}55 0%, transparent 65%); filter:blur(20px); }
    .pg-cover-grid { position:absolute; inset:0; background-image: linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px); background-size:40px 40px; opacity:0.6; }
    .pg-cover-inner { position:relative; padding:80px 70px; height:100%; display:flex; flex-direction:column; justify-content:space-between; }
    .pg-cover-top { display:flex; align-items:center; justify-content:space-between; }
    .pg-brand-mark { display:flex; align-items:center; gap:12px; font-weight:800; letter-spacing:2px; font-size:13px; text-transform:uppercase; color:#e2e8f0; }
    .pg-brand-dot { width:14px; height:14px; border-radius:4px; background:linear-gradient(135deg, ${accent}, ${accent2}); box-shadow:0 0 24px ${accent}66; }
    .pg-cover-edition { font-size:11px; letter-spacing:3px; color:${accent}; text-transform:uppercase; padding:8px 14px; border:1px solid ${accent}55; border-radius:999px; }
    .pg-cover-mid h1 { font-size:62px; line-height:1.02; font-weight:800; margin:0 0 22px; letter-spacing:-1.2px; color:#ffffff; max-width:620px; }
    .pg-cover-sub { font-size:18px; line-height:1.5; color:#cbd5e1; max-width:560px; margin:0 0 36px; font-weight:300; }
    .pg-cover-rule { width:80px; height:4px; background:linear-gradient(90deg, ${accent}, ${accent2}); border-radius:99px; margin-bottom:24px; }
    .pg-cover-meta { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; margin-top:28px; }
    .pg-meta-card { padding:18px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:14px; }
    .pg-meta-card .k { font-size:10px; letter-spacing:2px; text-transform:uppercase; color:${accent}; margin-bottom:8px; font-weight:700; }
    .pg-meta-card .v { font-size:18px; font-weight:700; color:#f1f5f9; }
    .pg-cover-bottom { display:flex; align-items:flex-end; justify-content:space-between; }
    .pg-cover-author { font-size:13px; color:#94a3b8; }
    .pg-cover-author b { color:#f1f5f9; display:block; font-size:15px; letter-spacing:0.5px; margin-bottom:4px; }
    .pg-cover-year { font-size:80px; font-weight:800; line-height:1; color:transparent; -webkit-text-stroke:1px ${accent}66; letter-spacing:-3px; opacity:0.7; }

    /* Page chrome */
    .pg-header { position:relative; z-index:2; min-height:${PAGE_HEADER_HEIGHT - 34}px; display:flex; align-items:center; justify-content:space-between; padding:22px 0 24px; margin:0 0 34px; border-bottom:1px solid #e2e8f0; gap:16px; overflow:visible; }
    .pg-header-left { display:flex; align-items:center; gap:10px; font-size:11px; line-height:1.45; letter-spacing:2px; text-transform:uppercase; color:#475569; font-weight:700; flex-shrink:0; max-width:330px; white-space:normal; }
    .pg-header-left .dot { width:10px; height:10px; border-radius:3px; background:linear-gradient(135deg, ${accent}, ${accent2}); }
    .pg-header-right { font-size:11px; line-height:1.45; letter-spacing:2px; text-transform:uppercase; color:#94a3b8; font-weight:600; max-width:330px; text-align:right; overflow:visible; white-space:normal; }

    /* Section banners (rotating gradients) */
    .pg-section-banner { position:relative; z-index:2; color:#f8fafc; padding:42px 42px 40px; border-radius:18px; margin:0 0 34px; overflow:visible; box-shadow:0 12px 28px -16px rgba(15,23,42,0.4); min-height:150px; }
    .pg-section-banner.v1 { background:linear-gradient(135deg, #0f172a 0%, #1e293b 100%); }
    .pg-section-banner.v2 { background:linear-gradient(135deg, #1e1b4b 0%, #312e81 100%); }
    .pg-section-banner.v3 { background:linear-gradient(135deg, #064e3b 0%, #065f46 100%); }
    .pg-section-banner.v4 { background:linear-gradient(135deg, #7c2d12 0%, #9a3412 100%); }
    .pg-section-banner.v5 { background:linear-gradient(135deg, #0c4a6e 0%, #075985 100%); }
    .pg-section-banner::after { content:""; position:absolute; right:-60px; top:-60px; width:200px; height:200px; background:radial-gradient(circle, ${accent}55 0%, transparent 70%); z-index:0; pointer-events:none; border-radius:50%; }
    .pg-section-banner > * { position:relative; z-index:1; }
    .pg-section-banner .pg-eyebrow { font-size:10px; line-height:1.6; letter-spacing:3px; text-transform:uppercase; color:${accent}; font-weight:800; margin-bottom:14px; }
    .pg-section-banner h2 { font-size:28px; line-height:1.32; margin:0; color:#ffffff; border:none; padding:0 0 2px; font-weight:800; letter-spacing:0; max-width:calc(100% - 90px); word-wrap:break-word; overflow-wrap:break-word; hyphens:auto; overflow:visible; }

    /* Typography */
    h1 { font-size: 36px; line-height:1.1; font-weight: 800; margin: 0 0 14px; color:#0f172a; letter-spacing:-0.5px; }
    h2 { font-size: 24px; line-height:1.35; font-weight: 800; margin: 28px 0 14px; padding:2px 0 12px; border-bottom: 2px solid #e2e8f0; color:#0f172a; overflow:visible; }
    h3 { font-size: 18px; line-height:1.35; font-weight: 800; margin: 24px 0 12px; color:#0f172a; overflow:visible; }
    h3.pg-module-title { display:flex; align-items:center; gap:10px; padding:14px 18px; background:linear-gradient(135deg, #fffdf5, #fff); border:1px solid #fde68a; border-left:5px solid ${accent}; border-radius:12px; box-shadow:0 4px 12px -8px rgba(245,185,66,0.4); }
    .pg-module-chip { color:${accent}; font-size:14px; }
    h4 { font-size: 14px; font-weight: 700; margin: 16px 0 8px; color:#0f172a; }
    p { margin: 0 0 10px; font-size:13px; line-height:1.65; color:#1f2937; word-wrap:break-word; overflow-wrap:break-word; }
    ul, ol { margin: 0 0 12px; padding-left: 22px; }
    li { margin-bottom: 6px; line-height:1.6; font-size:13px; color:#1f2937; }
    strong { color:#0f172a; font-weight:700; }
    em { color:#475569; }
    a { color:${accent}; text-decoration:none; border-bottom:1px solid ${accent}55; }

    li.pg-check { list-style:none; margin-left:-18px; padding-left:0; display:flex; align-items:flex-start; gap:10px; }
    .pg-box { display:inline-block; width:18px; height:18px; border:2px solid #cbd5e1; border-radius:5px; flex-shrink:0; margin-top:2px; }
    .pg-box-done { background:${accent}; border-color:${accent}; color:#ffffff; text-align:center; line-height:14px; font-weight:800; font-size:12px; }

    .pg-callout { margin:14px 0; padding:16px 18px 14px; border-radius:12px; box-shadow:0 6px 16px -10px rgba(15,23,42,0.25); }
    .pg-callout-head { display:flex; align-items:center; gap:10px; margin-bottom:8px; }
    .pg-callout-icon { display:inline-flex; width:24px; height:24px; align-items:center; justify-content:center; color:#fff; font-weight:800; border-radius:7px; font-size:13px; }
    .pg-callout-title { font-weight:800; font-size:12px; letter-spacing:1.2px; text-transform:uppercase; }
    .pg-callout-body { font-size:13px; line-height:1.6; }
    .pg-callout-body p { margin:0 0 6px; color:inherit; }
    .pg-callout-body p:last-child { margin-bottom:0; }

    code { background:#f1f5f9; padding:2px 6px; border-radius:4px; font-family:'JetBrains Mono','Menlo',monospace; font-size:12px; color:#0f172a; }
    pre { background:#0b1120; color:#e2e8f0; padding:20px 22px; border-radius:14px; font-family:'JetBrains Mono','Menlo',monospace; font-size:11px; line-height:1.55; overflow:hidden; white-space:pre-wrap; word-wrap:break-word; margin:14px 0; border:1px solid #1e293b; box-shadow:0 12px 24px -16px rgba(0,0,0,0.5); }
    pre::before { content:"FRAMEWORK"; display:block; font-size:9px; letter-spacing:3px; color:${accent}; margin-bottom:10px; font-family:'Inter',sans-serif; font-weight:800; }
    pre code { background:transparent; color:inherit; padding:0; }

    .pg-table-wrap { margin:14px 0; border:1px solid #e2e8f0; border-radius:12px; overflow:hidden; box-shadow:0 6px 16px -12px rgba(15,23,42,0.25); }
    .pg-table { width:100%; border-collapse:collapse; font-size:12px; }
    .pg-table th, .pg-table td { padding:10px 12px; text-align:left; vertical-align:top; border-bottom:1px solid #e2e8f0; }
    .pg-table th { background:linear-gradient(135deg,#0f172a,#1e293b); color:#f8fafc; font-weight:700; letter-spacing:0.5px; font-size:11px; text-transform:uppercase; }
    .pg-table tr:nth-child(even) td { background:#f8fafc; }
    .pg-table tr:last-child td { border-bottom:none; }

    blockquote { margin:14px 0; padding:14px 18px; background:linear-gradient(135deg,#fffbeb,#fef3c7); border-left:4px solid ${accent}; border-radius:8px; color:#78350f; font-weight:500; }
    blockquote p { margin:0; color:inherit; }

    .pg-divider { display:flex; justify-content:center; align-items:center; gap:12px; margin:24px 0; }
    .pg-divider span { width:6px; height:6px; border-radius:50%; background:${accent}; opacity:0.5; }
    .pg-divider span:nth-child(2) { opacity:1; transform:scale(1.3); }

    .pg-chapter-closer { margin-top:24px; }
    .pg-closer-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
    .pg-closer-card { padding:18px 20px; border-radius:16px; border:1px solid #e2e8f0; }
    .pg-closer-reflect { background:linear-gradient(180deg,#f5f3ff,#fff); border-color:#ddd6fe; }
    .pg-closer-goals { background:linear-gradient(180deg,#ecfdf5,#fff); border-color:#a7f3d0; }
    .pg-closer-eyebrow { font-size:10px; letter-spacing:3px; text-transform:uppercase; font-weight:800; margin-bottom:6px; }
    .pg-closer-reflect .pg-closer-eyebrow { color:#7c3aed; }
    .pg-closer-goals .pg-closer-eyebrow { color:#059669; }
    .pg-closer-title { font-size:14px; font-weight:800; color:#0f172a; margin-bottom:10px; }
    .pg-closer-list { list-style:none; padding:0; margin:0 0 10px; }
    .pg-closer-list li { font-size:12px; color:#1f2937; margin-bottom:6px; display:flex; gap:8px; align-items:flex-start; }
    .pg-q { color:#7c3aed; font-weight:800; }
    .pg-notes-lines { margin-top:8px; }
    .pg-notes-lines div { border-bottom:1px dashed #c4b5fd; height:16px; }
    .pg-week-list { list-style:none; padding:0; margin:0; }

    .pg-toc-row { display:flex; align-items:center; gap:14px; padding:11px 0; border-bottom:1px dashed #e2e8f0; }
    .pg-toc-num { font-weight:800; color:${accent}; font-size:13px; letter-spacing:1px; min-width:34px; }
    .pg-toc-title { font-weight:600; font-size:13px; color:#0f172a; flex:1; }
    .pg-toc-line { flex:0 0 auto; width:40px; border-bottom:1px dotted #cbd5e1; }
    .pg-toc-page { font-weight:800; color:#0f172a; font-size:13px; min-width:30px; text-align:right; }

    .pg-tips-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin:14px 0; }
    .pg-tips-card { padding:14px; border-radius:12px; border:1px solid #e2e8f0; background:linear-gradient(180deg,#fff,#f8fafc); box-shadow:0 4px 10px -8px rgba(15,23,42,0.2); }
    .pg-tips-card .k { display:flex; align-items:center; gap:8px; font-size:10px; letter-spacing:2px; text-transform:uppercase; font-weight:800; margin-bottom:6px; }
    .pg-tips-card .ico { display:inline-flex; width:18px; height:18px; align-items:center; justify-content:center; border-radius:5px; color:#fff; font-size:11px; font-weight:800; }
    .pg-tips-card .t { font-size:12px; font-weight:700; color:#0f172a; margin-bottom:4px; }
    .pg-tips-card .s { font-size:11px; color:#475569; line-height:1.5; }
    .pg-tips-card .pg { display:inline-block; margin-top:6px; font-size:10px; letter-spacing:1px; text-transform:uppercase; color:${accent}; font-weight:800; }
  `;
}

// ---------- Helpers ----------

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function escapeAttr(s: string): string { return escapeHtml(s); }

function removeAiGenerationReferences(value: string): string {
  return value
    .replace(/\bAI[-\s]*Generated(?:\s+Content)?\b/gi, "Premium Content")
    .replace(/\bGenerated\s+by\s+AI\b/gi, "Professionally Prepared")
    .replace(/\bCreated\s+with\s+AI\b/gi, "Professionally Prepared")
    .replace(/\bPowered\s+by\s+AI\b/gi, "Premium Edition")
    .replace(/\bAI\s+badge\b/gi, "premium badge")
    .replace(/\bAI\s+footer\b/gi, "premium footer")
    .replace(/\bAI\s+watermark\b/gi, "premium watermark")
    .replace(/\bAI-generated\b/gi, "premium")
    .replace(/[^\S\r\n]{2,}/g, " ")
    .trim();
}

interface CalloutMeta {
  id: string;
  kind: string;
  title: string;
  summary: string;
  sectionTitle: string;
  pdfPage: number;
}

const raf = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

// Shrink banner / heading titles that overflow their container. Premium "responsive" type sizing.
function autoFitTitles(root: HTMLElement) {
  const fit = (el: HTMLElement, maxFont: number, minFont: number, maxLines: number) => {
    let fs = maxFont;
    el.style.fontSize = `${fs}px`;
    let guard = 0;
    while (guard++ < 24) {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || fs * 1.25;
      const overflowsW = el.scrollWidth > el.clientWidth + 1;
      const lines = Math.round(el.getBoundingClientRect().height / lineHeight);
      if (!overflowsW && lines <= maxLines) break;
      if (fs <= minFont) break;
      fs -= 1;
      el.style.fontSize = `${fs}px`;
    }
  };
  root.querySelectorAll<HTMLElement>(".pg-section-banner h2").forEach((h) => fit(h, 28, 18, 3));
  root.querySelectorAll<HTMLElement>("h1").forEach((h) => fit(h, 36, 24, 3));
}

// Parse an HTML string into an array of top-level block HTML strings.
function splitTopLevelBlocks(html: string): string[] {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const out: string[] = [];
  Array.from(tmp.children).forEach((c) => out.push((c as HTMLElement).outerHTML));
  return out;
}

function outerHeight(el: HTMLElement): number {
  const style = getComputedStyle(el);
  return el.getBoundingClientRect().height + parseFloat(style.marginTop || "0") + parseFloat(style.marginBottom || "0");
}

function pageBlockCapacity(page: HTMLElement): number {
  const style = getComputedStyle(page);
  const innerHeight = page.clientHeight - parseFloat(style.paddingTop || "0") - parseFloat(style.paddingBottom || "0");
  const chromeHeight = Array.from(page.children).reduce((total, child) => {
    const el = child as HTMLElement;
    return el.hasAttribute("data-pg-idx") ? total : total + outerHeight(el);
  }, 0);
  return Math.max(240, innerHeight - chromeHeight - 22);
}

interface PageBuilderArgs {
  blocksHtml: string;
  isFirst: boolean;
  pageIndex: number;
  totalPages: number;
}

// Render a flow of blocks into the PDF, packing them into pages without splitting.
async function renderFlowToPdf(
  pdf: jsPDF,
  container: HTMLElement,
  blocksHtml: string[],
  buildPage: (args: PageBuilderArgs) => HTMLElement,
  isFirstPdfPage: boolean,
  calloutPageRecorder?: (id: string, pdfPage: number) => void,
): Promise<{ startPage: number; pageCount: number }> {
  // 1) Measure heights of each block in a hidden .pg-page measurer.
  const measurer = buildPage({ blocksHtml: blocksHtml.join(""), isFirst: true, pageIndex: 0, totalPages: 1 });
  measurer.style.position = "absolute";
  measurer.style.left = "-99999px";
  measurer.style.top = "0";
  measurer.style.visibility = "hidden";
  measurer.style.height = "auto";
  container.appendChild(measurer);
  await raf();
  autoFitTitles(measurer);
  await raf();
  const firstPageCapacity = pageBlockCapacity(measurer);
  const continuationMeasurer = buildPage({ blocksHtml: "", isFirst: false, pageIndex: 0, totalPages: 1 });
  continuationMeasurer.style.position = "absolute";
  continuationMeasurer.style.left = "-99999px";
  continuationMeasurer.style.top = "0";
  continuationMeasurer.style.visibility = "hidden";
  container.appendChild(continuationMeasurer);
  await raf();
  const continuationCapacity = pageBlockCapacity(continuationMeasurer);
  container.removeChild(continuationMeasurer);

  // The blocks live inside the measurer after the header (and any other chrome the builder adds).
  // We tagged each block with data-pg-idx for reliable lookup.
  const blockEls: HTMLElement[] = [];
  for (let i = 0; i < blocksHtml.length; i++) {
    const el = measurer.querySelector<HTMLElement>(`[data-pg-idx="${i}"]`);
    if (el) blockEls.push(el);
  }
  const heights = blockEls.map((el) => el.getBoundingClientRect().height);
  const calloutTops = new Map<string, { idx: number; top: number; el: HTMLElement }>();
  blockEls.forEach((el, idx) => {
    el.querySelectorAll<HTMLElement>(".pg-callout").forEach((co) => {
      calloutTops.set(co.id, { idx, top: co.offsetTop - el.offsetTop, el: co });
    });
  });
  container.removeChild(measurer);

  // 2) Pack blocks into pages.
  const pages: number[][] = [];
  let cur: number[] = [];
  let h = 0;
  for (let i = 0; i < blocksHtml.length; i++) {
    const bh = heights[i] || 0;
    const capacity = pages.length === 0 ? firstPageCapacity : continuationCapacity;
    if (bh > capacity) {
      // Oversized: place alone on its own page (will be canvas-sliced if absolutely needed).
      if (cur.length) { pages.push(cur); cur = []; h = 0; }
      pages.push([i]);
      continue;
    }
    if (h + bh > capacity && cur.length) {
      pages.push(cur);
      cur = [i];
      h = bh;
    } else {
      cur.push(i);
      h += bh;
    }
  }
  if (cur.length) pages.push(cur);
  if (pages.length === 0) pages.push([]);

  // 3) Render each page.
  const startPage = pdf.getNumberOfPages() + (isFirstPdfPage ? 0 : 1);
  let firstChunk = isFirstPdfPage;
  for (let p = 0; p < pages.length; p++) {
    const slice = pages[p].map((i) => blocksHtml[i]).join("");
    const node = buildPage({ blocksHtml: slice, isFirst: p === 0, pageIndex: p, totalPages: pages.length });
    container.appendChild(node);
    await raf();
    autoFitTitles(node);
    await raf();

    if (!firstChunk) pdf.addPage();
    firstChunk = false;

    const canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: node.scrollWidth,
      windowHeight: node.scrollHeight,
    });
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = pdf.internal.pageSize.getHeight();
    const imgH = (canvas.height * pdfW) / canvas.width;
    if (imgH <= pdfH + 0.5) {
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pdfW, imgH, undefined, "FAST");
    } else {
      // Fallback: oversized block — slice across multiple PDF pages.
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      let position = 0;
      let heightLeft = imgH;
      pdf.addImage(dataUrl, "JPEG", 0, position, pdfW, imgH, undefined, "FAST");
      heightLeft -= pdfH;
      while (heightLeft > 0) {
        position = heightLeft - imgH;
        pdf.addPage();
        pdf.addImage(dataUrl, "JPEG", 0, position, pdfW, imgH, undefined, "FAST");
        heightLeft -= pdfH;
      }
    }

    // Record callout pages
    if (calloutPageRecorder) {
      const absPdfPage = pdf.getNumberOfPages();
      pages[p].forEach((bidx) => {
        calloutTops.forEach((info, id) => {
          if (info.idx === bidx) calloutPageRecorder(id, absPdfPage);
        });
      });
    }

    container.removeChild(node);
  }

  return { startPage, pageCount: pages.length };
}

// Render a fixed full-bleed node (cover) as a single PDF page.
async function renderFixedNode(pdf: jsPDF, node: HTMLElement, isFirst: boolean): Promise<void> {
  if (!isFirst) pdf.addPage();
  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
    windowHeight: node.scrollHeight,
  });
  const pdfW = pdf.internal.pageSize.getWidth();
  const pdfH = pdf.internal.pageSize.getHeight();
  pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pdfW, pdfH, undefined, "FAST");
}

// ---------- Public API ----------

export async function renderPremiumGuidePdf(
  markdown: string,
  title: string,
  options: PremiumGuidePdfOptions = {},
): Promise<Blob> {
  const accent = options.accentColor || "#f5b942";
  const accent2 = options.accentSecondary || "#f97316";
  const cleanTitle = removeAiGenerationReferences(title || "Premium Blueprint") || "Premium Blueprint";
  const subtitle = removeAiGenerationReferences(options.subtitle || "A masterclass blueprint engineered for serious implementation.");
  const brand = options.brandName || "RUNOBLOG";
  const author = removeAiGenerationReferences(options.authorLine || "RunoBlog Editorial Team");
  const includeReflection = options.includeReflection !== false;
  const runQa = options.runQa !== false;
  const year = new Date().getFullYear();

  const sections = splitIntoSections(buildHtmlFromMarkdown(markdown, accent));
  if (sections.length > 0) {
    sections[0].body = sections[0].body.replace(/^<h1>[\s\S]*?<\/h1>/, "");
  }

  const container = document.createElement("div");
  container.className = "pg-root";
  container.style.position = "fixed";
  container.style.left = "-99999px";
  container.style.top = "0";
  container.style.background = "#ffffff";
  const styleTag = document.createElement("style");
  styleTag.textContent = premiumStyles(accent, accent2);
  container.appendChild(styleTag);
  document.body.appendChild(container);

  // Tag block HTMLs with data-pg-idx so the paginator can measure them reliably.
  const tagBlocks = (blocks: string[]): string[] =>
    blocks.map((b, i) => b.replace(/^<(\w+)/, `<$1 data-pg-idx="${i}"`));

  // Build a page-builder factory for content sections (with header chrome + optional banner).
  const buildContentPageFactory =
    (sectionLabel: string, sectionTitle: string, bannerHtml: string) =>
    ({ blocksHtml, isFirst }: PageBuilderArgs) => {
      const node = document.createElement("div");
      node.className = "pg-page";
      node.innerHTML = `
        <div class="pg-header">
          <div class="pg-header-left"><span class="dot"></span> ${escapeHtml(brand)} · ${escapeHtml(sectionLabel)}</div>
          <div class="pg-header-right">${escapeHtml(removeAiGenerationReferences(sectionTitle || cleanTitle))}</div>
        </div>
        ${isFirst ? bannerHtml : ""}
        ${blocksHtml}`;
      return node;
    };

  try {
    // ---------- PASS 1: dry-run pagination using a temp PDF (also collects callout pages) ----------
    const dryPdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
    const calloutById = new Map<string, { kind: string; title: string; summary: string; sectionTitle: string; pdfPage: number }>();

    // Pre-pages get rendered later; for dry-run we just track section start pages.
    // We'll prepend a placeholder count (cover + toc + tips) and refine below.
    let preCount = 3;

    const dryRunSections = async (): Promise<{ sectionStart: number[]; sectionPages: number[] }> => {
      const dp = new jsPDF({ unit: "pt", format: "a4", compress: true });
      // Add placeholder pre-pages so getNumberOfPages() math reflects later layout.
      for (let i = 0; i < preCount; i++) { if (i > 0) dp.addPage(); }
      const sectionStart: number[] = [];
      const sectionPages: number[] = [];
      let firstPdfPage = false; // pre-pages already exist
      for (let i = 0; i < sections.length; i++) {
        const s = sections[i];
        if (!s.body.trim() && !s.title) continue;
        const bannerVariant = `v${(i % 5) + 1}`;
        const banner = s.title
          ? `<div class="pg-section-banner ${bannerVariant}"><div class="pg-eyebrow">Section ${String(i + 1).padStart(2, "0")}</div><h2>${escapeHtml(s.title)}</h2></div>`
          : "";
        const cleanedBody = s.body.replace(/^<h2>[\s\S]*?<\/h2>/, "");
        const blocks = splitTopLevelBlocks(cleanedBody + (includeReflection ? chapterCloser(s.title) : ""));
        const tagged = tagBlocks(blocks);
        const sectionLabel = `Section ${String(i + 1).padStart(2, "0")}`;
        const builder = buildContentPageFactory(sectionLabel, s.title, banner);
        const recorder = (id: string, pdfPage: number) => {
          const co = calloutById.get(id);
          if (co) co.pdfPage = pdfPage;
          else calloutById.set(id, { kind: "note", title: "", summary: "", sectionTitle: s.title, pdfPage });
        };
        // Seed callout meta from DOM first (so we have title/kind/summary).
        const probe = document.createElement("div");
        probe.innerHTML = blocks.join("");
        probe.querySelectorAll<HTMLElement>(".pg-callout").forEach((co) => {
          calloutById.set(co.id, {
            kind: co.dataset.kind || "note",
            title: co.dataset.title || "Callout",
            summary: co.dataset.summary || "",
            sectionTitle: s.title,
            pdfPage: 0,
          });
        });

        const { startPage, pageCount } = await renderFlowToPdf(dp, container, tagged, builder, firstPdfPage, recorder);
        firstPdfPage = false;
        sectionStart.push(startPage);
        sectionPages.push(pageCount);
      }
      return { sectionStart, sectionPages };
    };

    let { sectionStart, sectionPages } = await dryRunSections();
    void dryPdf;
    void sectionPages;

    // ---------- Build TOC + Tips index nodes; measure their pagination too ----------
    const buildTocBlocks = (): string[] => {
      const items = sections
        .map((s, i) => ({ s, i }))
        .filter(({ s }) => !!s.title)
        .map(({ s, i }, displayIdx) => {
          const startInContent = sectionStart[displayIdx] || 0; // absolute page in dry-run pdf (already includes preCount)
          return `<div class="pg-toc-row">
            <span class="pg-toc-num">${String(i + 1).padStart(2, "0")}</span>
            <span class="pg-toc-title">${escapeHtml(s.title)}</span>
            <span class="pg-toc-line"></span>
            <span class="pg-toc-page">${String(startInContent).padStart(2, "0")}</span>
          </div>`;
        });
      const intro = `<div class="pg-section-banner v2"><div class="pg-eyebrow">Welcome</div><h2>How to use this blueprint</h2></div>
        <p>This is not a passive read — it's an implementation system. Each chapter ends with a Reflection &amp; Weekly Goals worksheet so you can integrate, not just consume.</p>
        <h3>Inside this blueprint</h3>`;
      // Intro is one block, then each TOC row is a block.
      return [intro, ...items];
    };

    const palette: Record<string, { bd: string; ico: string }> = {
      tip:     { bd: accent,    ico: "✦" },
      mistake: { bd: "#ef4444", ico: "⚠" },
      insight: { bd: "#3b82f6", ico: "◆" },
      action:  { bd: "#10b981", ico: "▶" },
      reflect: { bd: "#8b5cf6", ico: "✎" },
      note:    { bd: "#64748b", ico: "❝" },
    };
    const buildTipsBlocks = (): string[] => {
      const all = Array.from(calloutById.values());
      const order = ["tip", "mistake", "insight", "action", "reflect", "note"];
      const grouped = order
        .map((k) => ({ k, items: all.filter((c) => c.kind === k) }))
        .filter((g) => g.items.length);
      const intro = `<div class="pg-section-banner v3"><div class="pg-eyebrow">Index</div><h2>Pro Tips &amp; Warnings</h2></div>
        <p>Every tip, warning, insight and action step from this blueprint — collected with its page reference.</p>`;
      const blocks: string[] = [intro];
      grouped.forEach((g) => {
        const p = palette[g.k];
        blocks.push(`<h3>${labelForKind(g.k)} <span style="color:#94a3b8;font-weight:600;font-size:13px;">· ${g.items.length}</span></h3>`);
        // Emit cards in pairs for nice grid pagination.
        const cardHtmls = g.items.map((c) => `
          <div class="pg-tips-card">
            <div class="k"><span class="ico" style="background:${p.bd};">${p.ico}</span> ${escapeHtml(c.title)}</div>
            <div class="t">${escapeHtml(c.sectionTitle || "Blueprint")}</div>
            <div class="s">${escapeHtml(c.summary)}${c.summary.length >= 140 ? "…" : ""}</div>
            <span class="pg">Page ${String(c.pdfPage).padStart(2, "0")}</span>
          </div>`);
        for (let i = 0; i < cardHtmls.length; i += 2) {
          blocks.push(`<div class="pg-tips-grid">${cardHtmls[i]}${cardHtmls[i + 1] || ""}</div>`);
        }
      });
      if (grouped.length === 0) blocks.push(`<p><em>No callouts found in this blueprint.</em></p>`);
      return blocks;
    };

    const buildTocPageFactory = () => ({ blocksHtml }: PageBuilderArgs) => {
      const node = document.createElement("div");
      node.className = "pg-page";
      node.innerHTML = `
        <div class="pg-header">
          <div class="pg-header-left"><span class="dot"></span> ${escapeHtml(brand)} · Premium Blueprint</div>
          <div class="pg-header-right">Table of Contents</div>
        </div>
        ${blocksHtml}`;
      return node;
    };
    const buildTipsPageFactory = () => ({ blocksHtml }: PageBuilderArgs) => {
      const node = document.createElement("div");
      node.className = "pg-page";
      node.innerHTML = `
        <div class="pg-header">
          <div class="pg-header-left"><span class="dot"></span> ${escapeHtml(brand)} · Premium Blueprint</div>
          <div class="pg-header-right">Pro Tips &amp; Warnings</div>
        </div>
        ${blocksHtml}`;
      return node;
    };

    // Measure TOC + Tips pagination using the dry-run PDF.
    const measurePages = async (blocks: string[], factory: ReturnType<typeof buildTocPageFactory>) => {
      const dp = new jsPDF({ unit: "pt", format: "a4", compress: true });
      const tagged = tagBlocks(blocks);
      const { pageCount } = await renderFlowToPdf(dp, container, tagged, factory, true);
      return pageCount;
    };
    const tocPages = await measurePages(buildTocBlocks(), buildTocPageFactory());
    const tipsPages = await measurePages(buildTipsBlocks(), buildTipsPageFactory());
    const newPreCount = 1 /*cover*/ + tocPages + tipsPages;

    if (newPreCount !== preCount) {
      preCount = newPreCount;
      // Re-run section dry-run with corrected preCount so TOC page numbers are accurate.
      const result = await dryRunSections();
      sectionStart = result.sectionStart;
      sectionPages = result.sectionPages;
    }

    // ---------- PASS 2: assemble final PDF ----------
    const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
    pdf.setProperties({
      title: cleanTitle,
      subject: "Premium implementation blueprint",
      author,
      creator: brand,
      keywords: "premium blueprint, workbook, implementation guide",
    });

    // Cover
    const cover = document.createElement("div");
    cover.className = "pg-cover";
    cover.innerHTML = `
      <div class="pg-cover-glow"></div>
      <div class="pg-cover-glow-2"></div>
      <div class="pg-cover-grid"></div>
      <div class="pg-cover-inner">
        <div class="pg-cover-top">
          <div class="pg-brand-mark"><span class="pg-brand-dot"></span> ${escapeHtml(brand)}</div>
          <div class="pg-cover-edition">Premium Edition · ${year}</div>
        </div>
        <div class="pg-cover-mid">
          <div class="pg-cover-rule"></div>
          <div style="font-size:12px;letter-spacing:4px;text-transform:uppercase;color:${accent};margin-bottom:18px;font-weight:700;">The Blueprint</div>
          <h1>${escapeHtml(cleanTitle)}</h1>
          <p class="pg-cover-sub">${escapeHtml(subtitle)}</p>
          <div class="pg-cover-meta">
            <div class="pg-meta-card"><div class="k">Format</div><div class="v">Workbook</div></div>
            <div class="pg-meta-card"><div class="k">Level</div><div class="v">Beginner → Pro</div></div>
            <div class="pg-meta-card"><div class="k">Outcome</div><div class="v">Mastery</div></div>
          </div>
        </div>
        <div class="pg-cover-bottom">
          <div class="pg-cover-author"><b>${escapeHtml(author)}</b>Curated · Researched · Battle-tested</div>
          <div class="pg-cover-year">${year}</div>
        </div>
      </div>`;
    container.appendChild(cover);
    await raf();
    // Cover h1 can be long — auto-shrink from 62 down so it never clips the meta cards below.
    const coverH1 = cover.querySelector<HTMLElement>(".pg-cover-mid h1");
    if (coverH1) {
      let fs = 62;
      coverH1.style.fontSize = `${fs}px`;
      while (fs > 34 && coverH1.scrollHeight > 200) {
        fs -= 2;
        coverH1.style.fontSize = `${fs}px`;
      }
    }
    await raf();
    await renderFixedNode(pdf, cover, true);
    container.removeChild(cover);

    // TOC
    await renderFlowToPdf(pdf, container, tagBlocks(buildTocBlocks()), buildTocPageFactory(), false);
    // Tips index (now uses fresh callout pages from dry-run)
    await renderFlowToPdf(pdf, container, tagBlocks(buildTipsBlocks()), buildTipsPageFactory(), false);

    // Content sections
    const actualSectionStart: number[] = [];
    for (let i = 0; i < sections.length; i++) {
      const s = sections[i];
      if (!s.body.trim() && !s.title) continue;
      const bannerVariant = `v${(i % 5) + 1}`;
      const banner = s.title
        ? `<div class="pg-section-banner ${bannerVariant}"><div class="pg-eyebrow">Section ${String(i + 1).padStart(2, "0")}</div><h2>${escapeHtml(s.title)}</h2></div>`
        : "";
      const cleanedBody = s.body.replace(/^<h2>[\s\S]*?<\/h2>/, "");
      const blocks = splitTopLevelBlocks(cleanedBody + (includeReflection ? chapterCloser(s.title) : ""));
      const tagged = tagBlocks(blocks);
      const sectionLabel = `Section ${String(i + 1).padStart(2, "0")}`;
      const builder = buildContentPageFactory(sectionLabel, s.title, banner);
      const startBefore = pdf.getNumberOfPages() + 1;
      await renderFlowToPdf(pdf, container, tagged, builder, false);
      actualSectionStart.push(startBefore);
    }

    // Closing page
    const closing = document.createElement("div");
    closing.className = "pg-page";
    closing.innerHTML = `
      <div class="pg-header">
        <div class="pg-header-left"><span class="dot"></span> ${escapeHtml(brand)} · Premium Blueprint</div>
        <div class="pg-header-right">The End</div>
      </div>
      <div style="margin-top:80px;text-align:center;">
        <div style="font-size:11px;letter-spacing:4px;text-transform:uppercase;color:${accent};font-weight:800;margin-bottom:20px;">You have arrived</div>
        <h1 style="font-size:42px;letter-spacing:-1px;">Knowledge becomes power<br/>only when applied.</h1>
        <p style="max-width:520px;margin:24px auto 0;color:#475569;font-size:14px;line-height:1.7;">Don't just close this blueprint. Open your calendar. Schedule the first action. Then the next. Mastery is the compounding interest of small, brave executions.</p>
        <div class="pg-divider" style="margin:40px auto;"><span></span><span></span><span></span></div>
        <div style="display:inline-block;padding:18px 28px;border:1px solid ${accent}55;border-radius:14px;background:linear-gradient(135deg,#fffbeb,#fff);">
          <div style="font-size:10px;letter-spacing:3px;text-transform:uppercase;color:${accent};font-weight:800;margin-bottom:6px;">Your next step</div>
          <div style="font-size:18px;font-weight:700;color:#0f172a;">Start today. Iterate weekly. Master eternally.</div>
        </div>
      </div>`;
    container.appendChild(closing);
    await raf();
    await renderFixedNode(pdf, closing, false);
    container.removeChild(closing);

    // ---------- Footer page numbering on every page ----------
    const totalPages = pdf.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setDrawColor(255, 255, 255);
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(pageWidth / 2 - 70, pageHeight - 30, 140, 18, 9, 9, "F");
      pdf.setFontSize(8);
      pdf.setTextColor(15, 23, 42);
      const label = `${brand}  ·  ${String(i).padStart(2, "0")} / ${String(totalPages).padStart(2, "0")}`;
      pdf.text(label, pageWidth / 2, pageHeight - 18, { align: "center" });
    }

    if (runQa) {
      // eslint-disable-next-line no-console
      console.info("[premiumGuidePdf] v3 render complete", {
        totalPages,
        preCount,
        sections: sections.filter((s) => s.title).length,
        callouts: calloutById.size,
      });
    }

    return pdf.output("blob");
  } finally {
    document.body.removeChild(container);
  }
}

function labelForKind(k: string): string {
  switch (k) {
    case "tip": return "Pro Tips";
    case "mistake": return "Warnings & Mistakes to Avoid";
    case "insight": return "Key Insights";
    case "action": return "Action Steps";
    case "reflect": return "Reflections";
    default: return "Notes";
  }
}
